import { DetailsListLayoutMode, IColumn, IStyle, MessageBarType } from '@fluentui/react';
import {
  Button,
  buttonStylesPrimary,
  buttonStylesStealth,
  DetailsList,
  IH2OTheme,
  Link,
  linkStylesBlack,
  Loader,
  useClassNames,
  useTheme,
  useToast,
} from '@h2oai/ui-kit';
import React from 'react';
import { useHistory } from 'react-router-dom';

import { workspaceName } from '../../authz/gen/ai/h2o/workspace/v1/workspace_pb';
import { DateTimeShort } from '../../components/Date/DateTimeShort';
import { BaselineAggregation, ScoringAggregation } from '../../mlops/gen/ai/h2o/mlops/monitoring/v2/aggregate_pb';
import { LogicalType } from '../../mlops/gen/ai/h2o/mlops/monitoring/v2/logical_type_pb';
import { useMutationDeleteDeploymentMonitoring } from '../../mlops/mlops.deployer.hooks';
import { useQueryAggregates, useQueryBaselineAggregates } from '../../mlops/mlops.monitoring.hooks';
import { ClassNamesFromIStyles } from '../../utils/models';
import { formatDefaultDateAndTime, formatError } from '../../utils/utils';
import SearchableDropdown from '../Orchestrator/SearchableDropdown';
import { FormSection } from './BatchScoringDetail';
import { useConfirmDialog } from './ConfirmDialogProvider';
import { DeploymentTypeKey } from './DeploymentTabDetail';
import { BaselineAggregationFixed } from './Monitoring';
import { useGranularMLOpsPermissions } from './userActions';

interface LazyAggregatesTableProps {
  aggregates?: ScoringAggregation[];
  baselineAggregates?: BaselineAggregation[];
  type: 'aggregates' | 'baselineAggregates';
  hasNextPage: boolean;
  onLoadMore: () => void;
  isLoadingMore?: boolean;
}

interface ITableStyles {
  loadMore: IStyle;
  loadMoreLoader: IStyle;
}

const tableStyles = (theme: IH2OTheme): Partial<ITableStyles> => ({
  loadMore: {
    display: 'flex',
    justifyContent: 'center',
    padding: '20px 0',
    borderWidth: 1,
    borderTopStyle: 'solid',
    borderColor: theme.semanticColors?.inputBorder,
    borderRadius: 4,
    // TODO: Use theme colors.
    backgroundImage: 'linear-gradient(to top, rgba(255,255,255,0), rgba(255,255,255, 1) 90%)',
    '&:hover': {
      borderColor: theme.semanticColors?.buttonPrimaryBackground,
      cursor: 'pointer',
      button: {
        textDecoration: 'none',
      },
      'button:active': {
        textDecoration: 'none',
      },
    },
    span: {
      color: theme.semanticColors?.textSecondary,
    },
    '&:hover span': {
      color: theme.semanticColors?.textPrimary,
    },
  },
  loadMoreLoader: {
    display: 'flex',
    justifyContent: 'center',
    padding: '10px 0',
  },
});

export const splitCamelCase = (str: string) =>
  str
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
    .replace(/^./, (s) => s.toUpperCase());

export const LazyAggregatesTable: React.FC<LazyAggregatesTableProps> = ({
  aggregates,
  baselineAggregates,
  type,
  hasNextPage,
  onLoadMore,
  isLoadingMore = false,
}) => {
  const isBaseline = type === 'baselineAggregates',
    data = isBaseline ? baselineAggregates : aggregates,
    isNumerical = isBaseline
      ? (data?.[0] as BaselineAggregationFixed).logicalType === LogicalType.NUMERICAL
      : !!(data?.[0] as ScoringAggregation).numericalAggregate,
    isCategorical = isBaseline
      ? (data?.[0] as BaselineAggregationFixed).logicalType === LogicalType.CATEGORICAL
      : !!(data?.[0] as ScoringAggregation).categoricalAggregate,
    numerical = isBaseline
      ? (data?.[0] as BaselineAggregationFixed).numericalAggregate
      : (data?.[0] as ScoringAggregation).numericalAggregate,
    categorical = isBaseline
      ? (data?.[0] as BaselineAggregationFixed).categoricalAggregate
      : (data?.[0] as ScoringAggregation).categoricalAggregate,
    theme = useTheme(),
    classNames = useClassNames<ITableStyles, ClassNamesFromIStyles<ITableStyles>>(
      'aggregatesTable',
      tableStyles(theme)
    ),
    containerRef = React.useRef<HTMLDivElement>(null),
    [items, setItems] = React.useState<any[]>([]),
    [initialItemsCount, setInitialItemsCount] = React.useState<number>(0),
    columns: IColumn[] = React.useMemo(() => {
      const baseColumns: IColumn[] = isBaseline
        ? [
            {
              key: 'type',
              name: 'Type',
              fieldName: 'type',
              minWidth: 80,
              isResizable: true,
            },
          ]
        : [
            { key: 'startTime', name: 'Start Time', fieldName: 'startTime', minWidth: 150, isResizable: true },
            // TODO: Uncomment once backend support is added.
            // {
            //   key: 'segmentDuration',
            //   name: 'Segment Duration',
            //   fieldName: 'segmentDuration',
            //   minWidth: 150,
            //   isResizable: true,
            // },
          ];

      const missingColumn: IColumn = {
        key: 'missing',
        name: 'Missing',
        fieldName: 'missing',
        minWidth: 100,
        isResizable: true,
      };

      const numericalColumns: IColumn[] = isNumerical
        ? Object.keys(numerical || {}).map((key) => ({
            key: `numerical-${key}`,
            name: splitCamelCase(key),
            fieldName: key,
            minWidth: 100,
            isResizable: true,
          }))
        : [];

      const categoricalColumns: IColumn[] = isCategorical
        ? Object.keys(categorical?.valueCounts || {}).map((key) => ({
            key: `categorical-${key}`,
            name: splitCamelCase(key),
            fieldName: key,
            minWidth: 100,
            isResizable: true,
          }))
        : [];

      return [...baseColumns, missingColumn, ...numericalColumns, ...categoricalColumns];
    }, [isBaseline, isNumerical, isCategorical, numerical, categorical]);

  React.useEffect(() => {
    const items =
      (data || []).map((item) => {
        const isNumerical = isBaseline
            ? (item as BaselineAggregationFixed).logicalType === LogicalType.NUMERICAL
            : !!(item as ScoringAggregation).numericalAggregate,
          isCategorical = isBaseline
            ? (item as BaselineAggregationFixed).logicalType === LogicalType.CATEGORICAL
            : !!(item as ScoringAggregation).categoricalAggregate,
          numerical = isBaseline
            ? (item as BaselineAggregationFixed).numericalAggregate
            : (item as ScoringAggregation).numericalAggregate,
          categorical = isBaseline
            ? (item as BaselineAggregationFixed).categoricalAggregate
            : (item as ScoringAggregation).categoricalAggregate;

        const columnData = {
            key: item.id,
            column: item.column,
            type: isBaseline ? (isNumerical ? 'Numerical' : 'Categorical') : undefined,
            missing: item.missingValues?.rowCount ?? '-',
            startTime: isBaseline
              ? undefined
              : (item as ScoringAggregation)?.startTime
              ? formatDefaultDateAndTime((item as ScoringAggregation).startTime!)
              : '-',
            segmentDuration: isBaseline ? undefined : (item as ScoringAggregation).segmentDuration,
          },
          numericalColumnsData = isNumerical
            ? Object.keys(numerical || {}).reduce((acc: Record<string, any>, key: string) => {
                const value = (numerical as Record<string, any>)[key];
                acc[key] = Array.isArray(value) ? JSON.stringify(value) : value;
                return acc;
              }, {} as Record<string, any>)
            : {},
          categoricalColumnsData = isCategorical
            ? Object.keys(categorical?.valueCounts || {}).reduce((acc: Record<string, any>, key: string) => {
                const value = categorical?.valueCounts?.[key];
                acc[key] = Array.isArray(value) ? JSON.stringify(value) : value;
                return acc;
              }, {} as Record<string, any>)
            : {};
        return {
          ...columnData,
          ...numericalColumnsData,
          ...categoricalColumnsData,
        };
      }) || [];
    setItems(items);
  }, [data, isBaseline]);

  // TODO: Fix the scroll to bottom logic.
  React.useEffect(() => {
    // Scrolls to the bottom of the list when new items are fetched using "Load more...".
    if ((items || []).length > 0) {
      if (!initialItemsCount) {
        setInitialItemsCount((items || []).length);
      } else {
        const detailsList = document.querySelector('.h2o-Protected-main');
        if (detailsList && initialItemsCount < (items || []).length) {
          detailsList.scrollTop = containerRef.current?.scrollHeight ?? 0;
        }
      }
    }
  }, [isLoadingMore, containerRef.current, initialItemsCount, items]);

  return (
    <div style={{ height: '100%' }} ref={containerRef}>
      <DetailsList
        items={items}
        columns={columns}
        layoutMode={DetailsListLayoutMode.justified}
        headerStyles={{ root: { paddingTop: 0 } }}
      />
      {hasNextPage &&
        (isLoadingMore ? (
          <Loader className={classNames.loadMoreLoader} />
        ) : (
          <div className={classNames.loadMore} onClick={onLoadMore}>
            <Link styles={linkStylesBlack}>Load more...</Link>
          </div>
        ))}
    </div>
  );
};

export interface IDeploymentTabMonitoringStyles {
  form: IStyle;
  loader: IStyle;
  customEndpointButton: IStyle;
  infoLabel: IStyle;
  tooltip: IStyle;
  loaderWrapper: IStyle;
  sliderLabel: IStyle;
  sliderLabelLeft: IStyle;
  sliderLabelRight: IStyle;
  inlineItems: IStyle;
  tableContainer: IStyle;
  buttonActive: IStyle;
}

export const deploymentTabMonitoringStyles = (theme: IH2OTheme): Partial<IDeploymentTabMonitoringStyles> => ({
    form: {
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1,
      padding: 20,
      margin: '0px 10px',
      // TODO: Remove once theme is used.
      borderColor: theme.semanticColors?.bodyBackground,
    },
    loader: {
      display: 'flex',
      flexGrow: 1,
      alignItems: 'center',
      justifyContent: 'center',
    },
    sliderLabel: { display: 'flex', maxWidth: 460 },
    sliderLabelLeft: { flex: '1 1 0%', color: 'var(--h2o-gray500, #6B7280)', fontSize: 12 },
    sliderLabelRight: { flex: '1 1 0%', color: 'var(--h2o-gray500, #6B7280)', fontSize: 12, textAlign: 'right' },
    inlineItems: { display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'center' },
    tableContainer: {
      width: '100%',
      overflowY: 'auto',
    },
    buttonActive: {
      backgroundColor: theme.palette?.gray300,
      borderRadius: 6,
    },
  }),
  sliderContainerStyles = {
    root: {
      maxWidth: 460,
    },
    minMaxLabel: {
      display: 'none',
    },
    input: {
      display: 'none',
    },
  };

export interface IDeploymentTabMonitoringProps {
  columnNames?: string[];
  deploymentCreationTime?: Date;
  deploymentId: string;
  deploymentType: DeploymentTypeKey;
  experimentId?: string;
  secondaryExperimentId?: string;
  frontendUri?: string;
  workspaceId: string;
}

export const DeploymentTabMonitoring = ({
  columnNames,
  deploymentCreationTime,
  deploymentId,
  deploymentType,
  secondaryExperimentId,
  experimentId,
  frontendUri,
  workspaceId,
}: IDeploymentTabMonitoringProps) => {
  const currentWorkspaceFQDN = workspaceName.compile({ workspace: workspaceId });
  const permissions = useGranularMLOpsPermissions(currentWorkspaceFQDN);
  const { addToast } = useToast();
  const { showDialog } = useConfirmDialog();
  const history = useHistory();

  const [columnName, setColumnName] = React.useState(columnNames?.[0]);
  React.useEffect(() => {
    if (!columnName && columnNames?.length) {
      setColumnName(columnNames?.[0]);
    }
  }, [columnName, columnNames]);

  const [filterBy, setFilterBy] = React.useState<'last-hour' | 'last-6-hours' | 'last-24-hours' | 'all-time'>(
    `last-hour`
  );
  const fromTime = React.useMemo<string | undefined>(() => {
    const now = new Date();
    if (filterBy === 'all-time') {
      return deploymentCreationTime?.toISOString();
    }
    const beginDate =
      filterBy === 'last-hour'
        ? new Date(now.getTime() - 60 * 60 * 1000)
        : filterBy === 'last-6-hours'
        ? new Date(now.getTime() - 6 * 60 * 60 * 1000)
        : // filterBy === 'last-24-hours'
          new Date(now.getTime() - 24 * 60 * 60 * 1000);
    const mostRecentBeginDate =
      deploymentCreationTime && deploymentCreationTime > beginDate ? deploymentCreationTime : beginDate;
    return mostRecentBeginDate.toISOString();
  }, [filterBy, deploymentCreationTime]);
  const filterClause = React.useMemo<string>(() => {
    return [columnName && `column_name = '${columnName}'`, fromTime && `timestamp >= '${fromTime}'`]
      .filter(Boolean)
      .join(` AND `);
  }, [fromTime, columnName]);
  const aggregatesQuery = useQueryAggregates(
    {
      deploymentId,
      experimentId: experimentId || '',
      filter: filterClause,
      pageSize: 20,
      workspaceId,
    },
    {
      enabled: Boolean(columnName && permissions?.isAbleToViewDeployment),
      meta: {
        toastError: (err: unknown) => `Failed to fetch aggregates: ${formatError(err)}`,
      },
      refetchInterval: 5_000,
      select: (data) => {
        const flattenedAggregates = data.pages.flatMap((page) => page.aggregates || []);
        return { pages: flattenedAggregates, pageParams: data.pageParams };
      },
    }
  );
  const aggregates = aggregatesQuery.data?.pages;

  const secondaryAggregatesQuery = useQueryAggregates(
    {
      deploymentId,
      experimentId: secondaryExperimentId || '',
      filter: filterClause,
      pageSize: 20,
      workspaceId,
    },
    {
      enabled: Boolean(secondaryExperimentId && columnName && permissions?.isAbleToViewDeployment),
      meta: {
        toastError: (err: unknown) => `Failed to fetch aggregates: ${formatError(err)}`,
      },
      refetchInterval: 5_000,
      select: (data) => {
        const flattenedAggregates = data.pages.flatMap((page) => page.aggregates || []);
        return { pages: flattenedAggregates, pageParams: data.pageParams };
      },
    }
  );
  const secondaryAggregates = secondaryAggregatesQuery.data?.pages;

  const baselineAggregatesQuery = useQueryBaselineAggregates(
    {
      deploymentId,
      experimentId: experimentId || '',
      filter: `column_name = '${columnName}'`,
      pageSize: 20,
      workspaceId,
    },
    {
      enabled: Boolean(experimentId && columnName && permissions?.isAbleToViewDeployment),
      meta: {
        toastError: (err: unknown) => `Failed to fetch baseline aggregates: ${formatError(err)}`,
      },
      select: (data) => {
        const flattenedBaselineAggregates = data.pages.flatMap((page) => page.baselineAggregates || []);
        return { pages: flattenedBaselineAggregates, pageParams: data.pageParams };
      },
    }
  );
  const baselineAggregates = baselineAggregatesQuery.data?.pages;

  const deleteDeploymentMonitoringMutation = useMutationDeleteDeploymentMonitoring({
    onSuccess: () => {
      addToast({
        messageBarType: MessageBarType.success,
        message: 'Deployment monitoring data cleaned successfully.',
      });
      aggregatesQuery.refetch();
      if (secondaryExperimentId) secondaryAggregatesQuery.refetch();
      baselineAggregatesQuery.refetch();
      history.push(`/mlops/workspaces/${workspaceId}/deployments/${deploymentId}`);
    },
    onError: (err) => {
      addToast({
        messageBarType: MessageBarType.error,
        message: `Failed to clean deployment monitoring data: ${formatError(err)}`,
      });
    },
  });

  const handleCleanDeploymentData = () => {
    if (!deploymentId) return;
    deleteDeploymentMonitoringMutation.mutate({
      name: `workspaces/${workspaceId}/deployments/${deploymentId}`,
    });
  };

  const theme = useTheme(),
    classNames = useClassNames<IDeploymentTabMonitoringStyles, ClassNamesFromIStyles<IDeploymentTabMonitoringStyles>>(
      'deploymentTabMonitoringStyles',
      deploymentTabMonitoringStyles(theme)
    );

  return (
    <div className={classNames.form}>
      {permissions?.isAbleToViewDeployment ? (
        <>
          <div className={classNames.inlineItems} style={{ justifyContent: 'space-between' }}>
            {frontendUri && (
              <Button
                text="View in monitoring UI"
                iconProps={{
                  iconName: 'NavigateExternalInline',
                  styles: {
                    root: {
                      height: 20, // Centers the icon vertically.
                    },
                  },
                }}
                onClick={() => window.open(frontendUri.toString(), '_blank')}
                disabled={!frontendUri}
                styles={buttonStylesPrimary}
              />
            )}
            {permissions?.isAbleToUpdateDeployments ? (
              <Button
                text="Delete monitoring data"
                iconProps={{
                  iconName: 'Delete',
                  style: { color: 'var(--h2o-red400)' },
                }}
                onClick={() =>
                  showDialog?.({
                    title: 'Delete monitoring data?',
                    description: 'This action cannot be undone and will disable monitoring until re-enabled manually.',
                    onConfirm: () => {
                      handleCleanDeploymentData();
                    },
                  })
                }
                disabled={deleteDeploymentMonitoringMutation.isLoading}
                style={{
                  color: 'var(--h2o-red400)',
                  borderColor: 'var(--h2o-red400)',
                }}
                loading={deleteDeploymentMonitoringMutation.isLoading}
              />
            ) : null}
          </div>
          <div
            style={{
              height: 16,
            }}
          />
          <SearchableDropdown
            label="Column"
            disabled={!experimentId}
            value={columnName || ''}
            options={(columnNames || []).map((columnName) => ({
              key: columnName,
              text: columnName,
            }))}
            placeHolder="Select a column"
            searchBoxPlaceHolder="Search for a column"
            onChange={(option) => {
              setColumnName(option?.key as string);
            }}
            width={360}
            maxHeight={800}
          />
          <>
            <div className={classNames.inlineItems} style={{ marginTop: 20 }}>
              <div style={{ flexGrow: 1 }}>
                Results from {fromTime ? <DateTimeShort dateTime={fromTime} /> : `all time`}
              </div>
              <Button
                styles={buttonStylesStealth}
                text="Last hour"
                onClick={() => setFilterBy('last-hour')}
                className={filterBy === 'last-hour' ? classNames.buttonActive : undefined}
                disabled={aggregatesQuery.isLoading}
              />
              <Button
                styles={buttonStylesStealth}
                text="Last 6 hours"
                onClick={() => setFilterBy('last-6-hours')}
                className={filterBy === 'last-6-hours' ? classNames.buttonActive : undefined}
                disabled={aggregatesQuery.isLoading}
              />
              <Button
                styles={buttonStylesStealth}
                text="Last 24 hours"
                onClick={() => setFilterBy('last-24-hours')}
                className={filterBy === 'last-24-hours' ? classNames.buttonActive : undefined}
                disabled={aggregatesQuery.isLoading}
              />
              <Button
                styles={buttonStylesStealth}
                text="All time"
                onClick={() => setFilterBy('all-time')}
                className={filterBy === 'all-time' ? classNames.buttonActive : undefined}
                disabled={aggregatesQuery.isLoading}
              />
            </div>
          </>
          <FormSection title="Baseline" fullWidth>
            <div className={classNames.tableContainer}>
              {baselineAggregatesQuery.isLoading ? (
                <div className={classNames.loader}>
                  <Loader label="Loading baseline aggregates..." />
                </div>
              ) : (
                <div>
                  {baselineAggregates && baselineAggregates.length > 0 ? (
                    <LazyAggregatesTable
                      baselineAggregates={baselineAggregates}
                      type="baselineAggregates"
                      hasNextPage={Boolean(baselineAggregatesQuery.hasNextPage)}
                      onLoadMore={baselineAggregatesQuery.fetchNextPage}
                      isLoadingMore={baselineAggregatesQuery.isFetchingNextPage}
                    />
                  ) : (
                    <p>
                      No baseline aggregates found. Beware that it takes them some time for the aggregates to propagate
                      through the system. It should eventually appear here.
                    </p>
                  )}
                </div>
              )}
            </div>
          </FormSection>
          <FormSection title="Scoring Aggregates" fullWidth>
            <div>
              {aggregatesQuery.isLoading ||
              (deploymentType !== 'single_model' && secondaryAggregatesQuery.isLoading) ? (
                <div className={classNames.loader}>
                  <Loader label="Loading aggregates..." />
                </div>
              ) : (
                <div>
                  {(aggregates && aggregates.length > 0) || (secondaryAggregates && secondaryAggregates.length > 0) ? (
                    <>
                      {deploymentType !== 'single_model' ? (
                        <h4>
                          {deploymentType === 'ab_test'
                            ? 'Model A'
                            : deploymentType === 'champion_challenger'
                            ? 'Champion model'
                            : ''}
                        </h4>
                      ) : null}
                      {aggregates && aggregates.length > 0 ? (
                        <LazyAggregatesTable
                          aggregates={aggregates}
                          type="aggregates"
                          hasNextPage={Boolean(aggregatesQuery.hasNextPage)}
                          onLoadMore={aggregatesQuery.fetchNextPage}
                          isLoadingMore={aggregatesQuery.isFetchingNextPage}
                        />
                      ) : (
                        <p>No aggregates found.</p>
                      )}
                      {deploymentType !== 'single_model' ? (
                        <>
                          <h4>
                            {deploymentType === 'ab_test'
                              ? 'Model B'
                              : deploymentType === 'champion_challenger'
                              ? 'Challenger model'
                              : ''}
                          </h4>
                          {secondaryAggregates && secondaryAggregates.length > 0 ? (
                            <LazyAggregatesTable
                              aggregates={secondaryAggregates}
                              type="aggregates"
                              hasNextPage={Boolean(secondaryAggregatesQuery.hasNextPage)}
                              onLoadMore={secondaryAggregatesQuery.fetchNextPage}
                              isLoadingMore={secondaryAggregatesQuery.isFetchingNextPage}
                            />
                          ) : (
                            <p>No aggregates found.</p>
                          )}
                        </>
                      ) : null}
                    </>
                  ) : (
                    <p>
                      <b>No aggregates found for the selected time period.</b>
                      <br />
                      <br /> You need to score first and wait for scoring to be finished. If it did finish, stay tuned,
                      it takes them some time for aggregates to propagate through the system and it should eventually
                      appear here.
                    </p>
                  )}
                </div>
              )}
            </div>
          </FormSection>

          <div style={{ height: 20 }} />
        </>
      ) : (
        <p>
          You have no permissions to view deployment monitoring. Ask the workspace owner to grant you the necessary
          permissions.
        </p>
      )}
    </div>
  );
};
